import { useMemo, useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { Link } from "react-router";
import { useIntl } from "react-intl";
import { PersonResultRow } from "@/components/common/PersonResultRow";
import { incompleteConditionCount } from "./match-design-rules";
import { AVATAR_PLACEHOLDER_STYLE, avatarInitials } from "@/lib/avatar";
import { personDisplayName, personDisplayRole } from "@/lib/person-display";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { MultiSelect } from "@/components/design/MultiSelect";
import { PersonFilterBuilder } from "@/components/search/PersonFilterBuilder";
import type { OptionsByKey } from "@/lib/filter-fields";
import {
  CountriesDocument,
  GendersDocument,
  IndustriesDocument,
  MaritalStatusesDocument,
  MyPersonGroupsDocument,
  MyPinGroupsDocument,
  type PersonFilterInput,
  PinType,
  SharedItemSignUpsDocument,
} from "@/graphql/generated/operations";
import { useGlobalPersonSearch } from "@/lib/use-global-person-search";
import { useDebounced } from "@/lib/use-debounced";
import type { FieldOption } from "@/lib/field-util";
import { getLanguageOptions } from "@/lib/language-options";
import { useUiStore } from "@/stores/ui-store";
import {
  EMPTY_EXTENDED_FILTERS,
  buildExtendedWhere,
  type ExtendedPersonFilters,
} from "@/lib/search-filters";
import type { UseMatchEditorResult } from "./use-match-editor";
import { MATCH_CANDIDATE_LIMIT } from "./match-constants";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { LockClosedIcon, LockOpenIcon } from "./lock-icons";


const PAGE_SIZE = 50;

// Sentinel value used in the savedSets picker to mean "everything I've pinned",
// distinct from any real PinGroup id. Translated into `isPinned: true` in the query.
const ALL_PINNED_VALUE = "__all_pinned__";

interface SavedSet {
  id: string;
  kind: "personGroup" | "pinGroup" | "allPinned";
  name: string;
}

interface TopLevelFilters {
  savedSetIds: string[];
}

const EMPTY_TOP_LEVEL: TopLevelFilters = {
  savedSetIds: [],
};

function buildWhere(
  top: TopLevelFilters,
  extended: ExtendedPersonFilters,
  savedSetsById: Map<string, SavedSet>,
): PersonFilterInput | undefined {
  const and: PersonFilterInput[] = [];
  const clauses: PersonFilterInput[] = [];
  for (const id of top.savedSetIds) {
    const set = savedSetsById.get(id);
    if (!set) continue;
    if (set.kind === "personGroup") clauses.push({ personGroupId: { eq: id } });
    else if (set.kind === "pinGroup") clauses.push({ pinGroupId: { eq: id } });
    else if (set.kind === "allPinned") clauses.push({ isPinned: { eq: true } });
  }
  if (clauses.length === 1) and.push(clauses[0]!);
  else if (clauses.length > 1) and.push({ or: clauses });
  const extWhere = buildExtendedWhere(extended);
  if (extWhere) and.push(extWhere as PersonFilterInput);
  if (and.length === 0) return undefined;
  if (and.length === 1) return and[0];
  return { and };
}

export function PeopleStep({ editor }: { editor: UseMatchEditorResult }) {
  const intl = useIntl();
  const locale = useUiStore((s) => s.locale);
  const apollo = useApolloClient();
  const canEditContent =
    editor.myRole === "owner" || editor.myRole === "admin" || editor.myRole === "editor";
  const [top, setTop] = useState<TopLevelFilters>(EMPTY_TOP_LEVEL);
  const [extended, setExtended] = useState<ExtendedPersonFilters>(EMPTY_EXTENDED_FILTERS);
  const [builderKey, setBuilderKey] = useState(0);
  const [sampleNotice, setSampleNotice] = useState<{
    count: number;
    total: number | null;
  } | null>(null);

  const debouncedTop = useDebounced(top, 250);
  const debouncedExtended = useDebounced(extended, 250);

  const personGroupsQuery = useQuery(MyPersonGroupsDocument, { variables: { input: {} } });
  const pinGroupsQuery = useQuery(MyPinGroupsDocument, {
    variables: { input: { pinTypes: [PinType.Person] } },
  });

  const savedSets = useMemo<SavedSet[]>(() => {
    const out: SavedSet[] = [];
    const pgResult = personGroupsQuery.data?.personGroups;
    if (pgResult?.__typename === "PersonGroupResult") {
      for (const g of pgResult.personGroups ?? []) {
        out.push({ id: g.id, kind: "personGroup", name: g.itemCount != null ? `${g.name} · ${g.itemCount}` : g.name });
      }
    }
    out.push({
      id: ALL_PINNED_VALUE,
      kind: "allPinned",
      name: intl.formatMessage({
        id: "match.people.allPinned",
        defaultMessage: "All pinned",
      }),
    });
    const pinResult = pinGroupsQuery.data?.pinGroups;
    if (pinResult?.__typename === "PinGroupResult") {
      for (const g of pinResult.pinGroups ?? []) {
        out.push({ id: g.id, kind: "pinGroup", name: g.itemCount != null ? `${g.name} · ${g.itemCount}` : g.name });
      }
    }
    return out;
  }, [intl, personGroupsQuery.data, pinGroupsQuery.data]);

  const savedSetsById = useMemo(() => {
    const m = new Map<string, SavedSet>();
    for (const s of savedSets) m.set(s.id, s);
    return m;
  }, [savedSets]);

  const savedSetOptions = useMemo<FieldOption[]>(
    () =>
      savedSets.map((s) => ({
        value: s.id,
        label: s.name,
        section:
          s.kind === "personGroup"
            ? intl.formatMessage({ id: "match.people.personGroups", defaultMessage: "Groups" })
            : intl.formatMessage({ id: "match.people.pinGroups", defaultMessage: "Pin groups" }),
        glyph: s.kind === "allPinned" ? "★" : undefined,
      })),
    [intl, savedSets],
  );

  const where = useMemo(
    () => buildWhere(debouncedTop, debouncedExtended, savedSetsById),
    [debouncedTop, debouncedExtended, savedSetsById],
  );

  const { data: gendersData } = useQuery(GendersDocument);
  const { data: maritalData } = useQuery(MaritalStatusesDocument);
  const { data: countriesData } = useQuery(CountriesDocument);
  const { data: industriesData } = useQuery(IndustriesDocument);

  const optionsByKey: OptionsByKey = useMemo(() => {
    const gender: readonly FieldOption[] = (gendersData?.genders.genders ?? []).map((g) => ({
      value: g.id,
      label: g.name ?? g.id,
    }));
    const marital: readonly FieldOption[] = (maritalData?.maritalStatuses.maritalStatuses ?? []).map(
      (m) => ({
        value: m.id,
        label: m.name ?? m.id,
      }),
    );
    const country: readonly FieldOption[] = (countriesData?.countries.countries ?? []).map((c) => ({
      value: c.id,
      label: c.name ?? c.iso2 ?? c.id,
    }));
    const industry: readonly FieldOption[] = (industriesData?.industries.industries ?? []).map((i) => ({
      value: i.id,
      label: i.localizedName?.localized ?? i.name ?? i.id,
    }));
    return { gender, marital, country, language: getLanguageOptions(locale), industry };
  }, [gendersData, maritalData, countriesData, industriesData, locale]);

  // Person Finder runs through genesis' globalSearch (genesis#98): the free-text
  // `q` is the cross-fragment ranked query, `where` (savedSets + structured
  // filters, q excluded by buildExtendedWhere) becomes the server-side
  // personFilter. globalSearch deliberately exposes hasNextPage, NOT a total
  // count (counting would force a full scan), so the "add all" affordance below
  // works off hasNextPage instead of a totalCount.
  const search = useGlobalPersonSearch({
    query: debouncedExtended.q,
    personFilter: where ?? null,
    pageSize: PAGE_SIZE,
  });

  const directoryNodes = search.nodes;
  const hasNextPage = search.hasMore;

  const candidateRows = editor.candidates;
  const candidateByPersonId = useMemo(() => {
    const map = new Map<string, (typeof candidateRows)[number]>();
    for (const c of candidateRows) if (c.person) map.set(c.person.id, c);
    return map;
  }, [candidateRows]);

  const lockedCount = useMemo(
    () => candidateRows.filter((c) => c.locked).length,
    [candidateRows],
  );
  const teamSize = editor.data.teamSize ?? 0;

  // Locking is an Optimize-only concept (#134): Assess evaluates the pool as-added,
  // Rank v1 scores candidates independently (locked-team Rank is deferred).
  const isOptimize = editor.data.matchMode !== "ASSESS" && editor.data.matchMode !== "RANK";

  // Assistant frame (view-state only, no PII): mirror the Candidates view. Locking is Optimize-only,
  // so the locked count is omitted for Assess/Rank; team size isn't shown on this view, so it's out.
  useAssistantContext([
    {
      description: "the match candidate pool the user is editing",
      value:
        `${candidateRows.length} candidates in the pool` +
        (isOptimize ? `; ${lockedCount} locked` : ""),
    },
  ]);
  // Unvollstaendige Kriterien (leere Auswahl, z.B. Branche) blocken auch diesen
  // Direkt-Trigger, nicht nur das PreSolve-Gate: genesis wirft auf leere UUIDs.
  const canAssessLockedTeam =
    isOptimize && teamSize > 0 && lockedCount === teamSize && incompleteConditionCount(editor.data) === 0;

  // SharedItem stat fields (signUps, …) don't resolve when nested under
  // Match.sharedItem — genesis only populates them via a SharedItem query's
  // local context. So read the count from the dedicated sharedItemSignUps
  // query (totalCount is computed independently) rather than sharedItem.signUps.
  // It drives the "Interessenten hinzufügen" button; addAllInteressenten()
  // re-fetches the ids when clicked.
  const sharedItemId = editor.sharedItem?.id ?? null;
  const { data: signUpData } = useQuery(SharedItemSignUpsDocument, {
    variables: { input: { sharedItemId: sharedItemId ?? "", first: 1 } },
    skip: !canEditContent || sharedItemId == null,
    fetchPolicy: "cache-and-network",
  });
  const linkedSignUps = signUpData?.sharedItemSignUps.totalCount ?? 0;
  const showInteressenten =
    canEditContent && editor.sharedItem != null && linkedSignUps > 0;

  // Fully-locked shortcut: jump straight to the result and evaluate the team in the
  // match's OWN mode. #150 — an Optimize match must render Optimize's degenerate
  // evaluation of the fixed team, not silently switch to Assess. assessOnly is already
  // synced to the immutable matchMode by MatchWorkflow, so we don't touch it here.
  async function evaluateLockedTeam() {
    editor.setStepId("result");
    await editor.runSolver();
  }

  const sortedCandidates = useMemo(
    () =>
      [...candidateRows].sort((a, b) => {
        const al = a.locked ?? false;
        const bl = b.locked ?? false;
        if (al === bl) return 0;
        return al ? -1 : 1;
      }),
    [candidateRows],
  );

  const addAllLabel = useMemo(() => {
    if (directoryNodes.length === 0) {
      return intl.formatMessage({ id: "match.noMatchesEmpty", defaultMessage: "No matches" });
    }
    // globalSearch has no total count. When the whole result set is loaded
    // (no further page) directoryNodes.length is the exact match count. While
    // more pages exist the count is unknown AND addAllFiltered only pulls up to
    // MATCH_CANDIDATE_LIMIT, so the label promises "up to N", never "all" —
    // otherwise a >N result set would claim to add everything but silently cap.
    const exact = hasNextPage ? null : directoryNodes.length;
    if (exact == null) {
      return intl.formatMessage(
        { id: "match.people.addUpTo", defaultMessage: "Add up to {count} matching" },
        { count: MATCH_CANDIDATE_LIMIT },
      );
    }
    const extActive = buildExtendedWhere(extended) != null;
    if (top.savedSetIds.length === 1 && !extActive) {
      const set = savedSetsById.get(top.savedSetIds[0]!);
      if (set?.kind === "allPinned") {
        return intl.formatMessage(
          { id: "match.people.addAllPinned", defaultMessage: "Add all {count} pinned" },
          { count: exact },
        );
      }
      if (set) {
        const cleanName = set.name.split(" · ")[0];
        return intl.formatMessage(
          { id: "match.people.addAllFromSet", defaultMessage: "Add all {count} from {name}" },
          { count: exact, name: cleanName },
        );
      }
    }
    return intl.formatMessage(
      { id: "match.people.addAll", defaultMessage: "Add all {count}" },
      { count: exact },
    );
  }, [intl, directoryNodes.length, hasNextPage, top, extended, savedSetsById]);

  async function addById(personId: string) {
    if (!canEditContent) return;
    if (candidateByPersonId.has(personId)) return;
    setSampleNotice(null);
    await editor.addCandidates([personId]);
  }

  async function addAllFiltered() {
    if (!canEditContent) return;
    if (directoryNodes.length === 0) return;
    // Pull the full ranked match set (up to the candidate cap) in one shot;
    // `truncated` means more matched than the cap could add.
    const { ids: matchedIds, capped: truncated } =
      await search.fetchAllIds(MATCH_CANDIDATE_LIMIT);
    const ids = matchedIds.filter((id) => !candidateByPersonId.has(id));
    if (ids.length > 0) await editor.addCandidates(ids);
    setSampleNotice(truncated ? { count: MATCH_CANDIDATE_LIMIT, total: null } : null);
  }

  async function addAllInteressenten() {
    if (!canEditContent) return;
    const si = editor.sharedItem;
    if (!si) return;
    const res = await apollo.query({
      query: SharedItemSignUpsDocument,
      variables: { input: { sharedItemId: si.id, first: MATCH_CANDIDATE_LIMIT } },
      fetchPolicy: "no-cache",
    });
    const all = res.data?.sharedItemSignUps.nodes ?? [];
    const total = res.data?.sharedItemSignUps.totalCount ?? all.length;
    const ids = all.filter((p) => !candidateByPersonId.has(p.id)).map((p) => p.id);
    if (ids.length > 0) await editor.addCandidates(ids);
    setSampleNotice(
      total > MATCH_CANDIDATE_LIMIT
        ? { count: Math.min(total, MATCH_CANDIDATE_LIMIT), total }
        : null,
    );
  }

  async function removeCandidate(candidateId: string) {
    if (!canEditContent) return;
    setSampleNotice(null);
    await editor.removeCandidates([candidateId]);
  }

  async function toggleLock(candidateId: string, locked: boolean) {
    if (!canEditContent) return;
    await editor.toggleLock(candidateId, locked);
  }

  async function clearAll() {
    if (!canEditContent) return;
    setSampleNotice(null);
    await editor.removeCandidates(candidateRows.map((c) => c.id));
  }

  function resetAllFilters() {
    setTop(EMPTY_TOP_LEVEL);
    setExtended(EMPTY_EXTENDED_FILTERS);
    setBuilderKey((k) => k + 1);
  }

  const anyFilterActive =
    extended.q.trim().length > 0 ||
    top.savedSetIds.length > 0 ||
    buildExtendedWhere({ ...extended, q: "" }) != null;

  return (
    <div>
      <p className="max-w-xl text-sm text-ink/55">
        {intl.formatMessage({
          id: "match.people.intro",
          defaultMessage: "Search and filter people from your company and add them as candidates.",
        })}
        {isOptimize && (
          <>
            {" "}
            {intl.formatMessage({
              id: "match.people.introLock",
              defaultMessage: "Mark individual people as locked candidates when they must be part of the team.",
            })}
          </>
        )}
      </p>

      {/* Fill the viewport on desktop so as many people as possible show at
          once, with no dead vertical space below. Both columns stretch to the
          same height; each box is a flex column whose list grows to fill. */}
      <div className="mt-6 grid grid-cols-1 gap-5 @lg:grid-cols-2 @lg:h-[calc(100dvh-320px)] @lg:min-h-[480px]">
        <div className="box flex flex-col bg-surface p-5 @lg:min-h-0">
          <BoxHeader
            title={intl.formatMessage({
              id: "match.people.finderTitle",
              defaultMessage: "Person Finder",
            })}
            right={
              anyFilterActive ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetAllFilters}
                >
                  {intl.formatMessage({ id: "common.reset", defaultMessage: "Reset" })}
                </Button>
              ) : null
            }
          />

          {showInteressenten && editor.sharedItem ? (
            <div className="mb-4 flex items-center justify-between gap-3 border-b border-ink/10 pb-4">
              <div className="min-w-0">
                <Link
                  to={`/share/discover/${editor.sharedItem.id}`}
                  className="text-[12px] text-ink/70 underline-offset-2 hover:text-ink hover:underline"
                  title={intl.formatMessage({
                    id: "match.challenge.openLinkedShare",
                    defaultMessage: "Open linked share item",
                  })}
                >
                  ↗{" "}
                  {editor.sharedItem.name ||
                    intl.formatMessage({
                      id: "match.challenge.unnamedSharedItem",
                      defaultMessage: "Unnamed share item",
                    })}
                </Link>
                <div className="text-[11px] text-ink/45">
                  {intl.formatMessage({
                    id: "match.people.interestedExplain",
                    defaultMessage: "People who showed interest in this project",
                  })}
                </div>
              </div>
              <Button
                variant="secondary"
                size="compact"
                className="shrink-0"
                onClick={() => void addAllInteressenten()}
              >
                {intl.formatMessage(
                  {
                    id: "match.people.addInterested",
                    defaultMessage: "Add interested people ({count})",
                  },
                  { count: linkedSignUps },
                )}
              </Button>
            </div>
          ) : null}

          <input
            value={extended.q}
            onChange={(e) => setExtended((f) => ({ ...f, q: e.target.value }))}
            placeholder={intl.formatMessage({
              id: "match.people.searchPlaceholder",
              defaultMessage: "Name, job title …",
            })}
            className="w-full rounded bg-surface px-3 py-2 text-[13px] text-ink placeholder:text-ink/40 ring-1 ring-ink/10 focus:outline-none focus:ring-1 focus:ring-ink/30"
          />

          <div className="mt-3">
            <MultiSelect
              options={savedSetOptions}
              selected={top.savedSetIds}
              onChange={(next) => setTop({ savedSetIds: next })}
              placeholder={intl.formatMessage({
                id: "match.people.savedSetsPlaceholder",
                defaultMessage: "Groups & pins …",
              })}
              searchable
            />
          </div>

          <div className="mt-3 rounded-md bg-surface ring-1 ring-ink/10">
            <PersonFilterBuilder
              key={builderKey}
              person={extended}
              setPerson={setExtended}
              optionsByKey={optionsByKey}
            />
          </div>

          <div className="mt-6 flex items-center justify-end">
            <Button
              variant="primary"
              size="compact"
              onClick={() => void addAllFiltered()}
              disabled={!canEditContent || directoryNodes.length === 0}
            >
              {addAllLabel}
            </Button>
          </div>

          {/* pb-16 here and in the candidate box: the sticky step navigation
              (MatchWorkflow) floats over the list's bottom edge, the padding
              lets the last row scroll clear of it. */}
          <div className="mt-3 max-h-[480px] overflow-auto p-1 pb-16 @lg:max-h-none @lg:min-h-0 @lg:flex-1">
            {directoryNodes.length === 0 ? (
              <div className="bg-page box-flat py-12 text-center text-[12px] text-ink/45">
                {search.loading
                  ? intl.formatMessage({ id: "common.loading", defaultMessage: "Loading…" })
                  : intl.formatMessage({ id: "match.noPeopleFound", defaultMessage: "No people found" })}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {directoryNodes.map((p) => {
                  const added = candidateByPersonId.has(p.id);
                  const name = personDisplayName(p);
                  return (
                    <PersonResultRow
                      key={p.id}
                      person={{
                        id: p.id,
                        name,
                        avatarContent: p.avatar?.content ?? null,
                        avatarMime: p.avatar?.mimeType ?? null,
                      }}
                      title={name}
                      sub={personDisplayRole(p)}
                      embedded
                      actions={
                        <Button
                          variant={added ? "chip" : "primary"}
                          size="sm"
                          onClick={() => void addById(p.id)}
                          disabled={!canEditContent || added}
                        >
                          {added
                            ? intl.formatMessage({ id: "match.people.added", defaultMessage: "Added" })
                            : intl.formatMessage({ id: "common.add", defaultMessage: "Add" })}
                        </Button>
                      }
                    />
                  );
                })}
              </div>
            )}
            {hasNextPage ? (
              <div className="mt-3 text-center">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => search.loadMore()}
                >
                  {intl.formatMessage({ id: "common.loadMore", defaultMessage: "Load more" })}
                </Button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="box flex flex-col bg-surface p-5 @lg:min-h-0">
          <BoxHeader
            title={
              <span>
                {intl.formatMessage({
                  id: "match.people.selectedCandidates",
                  defaultMessage: "Selected Candidates",
                })}
                <span className="ml-2 text-[12px] font-normal text-ink/45 tabular-nums">
                  · {candidateRows.length}
                </span>
              </span>
            }
            right={
              candidateRows.length > 0 ? (
                <div className="flex items-center gap-4">
                  {canAssessLockedTeam && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => void evaluateLockedTeam()}
                      title={intl.formatMessage({
                        id: "match.people.evaluateLockedTitle",
                        defaultMessage: "Evaluate this fully-locked team",
                      })}
                    >
                      {intl.formatMessage({
                        id: "match.people.evaluateLocked",
                        defaultMessage: "Evaluate this team",
                      })}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void clearAll()}
                    disabled={!canEditContent}
                  >
                    {intl.formatMessage({ id: "match.removeAll", defaultMessage: "Remove all" })}
                  </Button>
                </div>
              ) : null
            }
          />

          {editor.candidatesLoading && candidateRows.length === 0 ? (
            <div className="py-14 text-center text-[13px] text-ink/55">
              {intl.formatMessage({ id: "common.loading", defaultMessage: "Loading…" })}
            </div>
          ) : candidateRows.length === 0 ? (
            <div className="py-14 text-center">
              <div className="mb-2 text-2xl text-ink/30">◯</div>
              <div className="text-[13px] text-ink/55">
                {intl.formatMessage({
                  id: "match.people.emptyCandidates",
                  defaultMessage: "No candidates yet – add them on the left via search or filters.",
                })}
              </div>
            </div>
          ) : (
            <>
              {sampleNotice ? (
                <div className="mb-3 rounded-md bg-ink/[0.06] px-3 py-2 text-[12px] text-ink/55">
                  {sampleNotice.total != null
                    ? intl.formatMessage(
                        {
                          id: "match.people.sampleNotice",
                          defaultMessage:
                            "Candidate pool contains the first {count} of {total} matching people from the last bulk add.",
                        },
                        { count: sampleNotice.count, total: sampleNotice.total },
                      )
                    : intl.formatMessage(
                        {
                          id: "match.people.sampleNoticeCapped",
                          defaultMessage:
                            "Candidate pool contains the first {count} matching people; more matched than could be added at once.",
                        },
                        { count: sampleNotice.count },
                      )}
                </div>
              ) : null}
              {/* Same overflow model as the Person Finder: a capped scroll box,
                  not page-flip. All candidates are client-side, so there's
                  nothing to page in — scrolling matches the left panel and
                  keeps both boxes the same height. */}
              <div className="max-h-[480px] overflow-auto p-1 pb-16 @lg:max-h-none @lg:min-h-0 @lg:flex-1">
                <div className="grid grid-cols-3 gap-2 @sm:grid-cols-4 @lg:grid-cols-5">
                  {sortedCandidates.map((c) => {
                    if (!c.person) return null;
                    const p = c.person;
                    const name = personDisplayName(p);
                    return (
                      <CandidateTile
                        key={c.id}
                        personId={p.id}
                        name={name}
                        avatarContent={p.avatar?.content ?? null}
                        avatarMime={p.avatar?.mimeType ?? null}
                        locked={c.locked ?? false}
                        onToggleLock={
                          isOptimize && canEditContent
                            ? () => void toggleLock(c.id, !(c.locked ?? false))
                            : undefined
                        }
                        onRemove={canEditContent ? () => void removeCandidate(c.id) : undefined}
                      />
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CandidateTile({
  personId,
  name,
  avatarContent,
  avatarMime,
  locked,
  onToggleLock,
  onRemove,
}: {
  personId: string;
  name: string;
  avatarContent: string | null;
  avatarMime: string | null;
  locked: boolean;
  onToggleLock?: () => void;
  onRemove?: () => void;
}) {
  const intl = useIntl();
  const lockLabel = locked
    ? intl.formatMessage({ id: "match.people.unlock", defaultMessage: "Unlock" })
    : intl.formatMessage({ id: "match.people.lockAsFix", defaultMessage: "Mark as fix in team" });
  const removeLabel = intl.formatMessage({ id: "common.remove", defaultMessage: "Entfernen" });
  const hasAvatar = Boolean(avatarContent && avatarMime);
  return (
    <div className="group relative flex flex-col overflow-hidden rounded-md bg-surface ring-1 ring-ink/5 transition hover:ring-accent/40">
      <Link
        to={`/p/${personId}`}
        aria-label={name}
        className="relative block aspect-square w-full overflow-hidden"
      >
        {hasAvatar ? (
          <img
            src={`data:${avatarMime};base64,${avatarContent}`}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center font-serif text-3xl"
            style={AVATAR_PLACEHOLDER_STYLE}
          >
            {avatarInitials(name)}
          </div>
        )}
      </Link>

      {onToggleLock && (
        <Button
          variant="icon"
          size="sm"
          onClick={onToggleLock}
          aria-pressed={locked}
          aria-label={lockLabel}
          title={lockLabel}
          className={`absolute left-2 top-2 bg-surface/90 shadow-sm backdrop-blur focus-visible:opacity-100 ${
            locked ? "" : "opacity-0 group-hover:opacity-100"
          }`}
        >
          {locked ? <LockClosedIcon /> : <LockOpenIcon />}
        </Button>
      )}

      {onRemove && (
        <Button
          variant="icon"
          size="sm"
          onClick={onRemove}
          aria-label={removeLabel}
          title={removeLabel}
          className="absolute right-2 top-2 bg-surface/90 opacity-0 shadow-sm backdrop-blur group-hover:opacity-100 focus-visible:opacity-100"
        >
          <span className="text-[14px] leading-none">✕</span>
        </Button>
      )}

      <div className="truncate bg-surface px-2 py-2 text-center text-[13px] font-medium leading-tight text-ink">
        {name}
      </div>
    </div>
  );
}

